import { useState } from "react";
import { Cap, Level } from "../utils/rbacCatalog";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { Helmet } from "react-helmet-async";
import EntityCheck from "../components/pages/dashboard/EntityCheck";
import { useGetEntityStatus, useGetEntityOwnerData } from "../hooks/entity";
import UsersStatsChart from "../components/pages/dashboard/UsersStatsChart";
import WelcomeHub from "../components/pages/dashboard/home/WelcomeHub";
import { entityStatusLineKey } from "../components/pages/dashboard/home/entityStatusLine";
import TrialOfferBanner from "../components/pages/dashboard/home/TrialOfferBanner";
import ActiveTrialBanner from "../components/molecules/ActiveTrialBanner";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../components/atoms/CustomButton";
import { ModalShell } from "../components/atoms/ModalShell";
import { useCreateEntityPreferencesWithSpecialKey } from "../hooks/entity";
import { useGetEntityUser, useGetCountUsers } from "../hooks/users";
import SecureStorage from "../utils/secureStorage";
import { AddCreditModal } from "../components/pages/dashboard/entities/AddCreditModal";
import SmsCreditWarning from "../components/pages/dashboard/home/SmsCreditWarning";
import WhatsappCreditWarning from "../components/pages/dashboard/home/WhatsappCreditWarning";
import { useGetWhatsappConfig } from "../hooks/messages";
import PasskeyEnrolmentPrompt from "../components/pages/dashboard/home/PasskeyEnrolmentPrompt";
import AskAiSearch from "../components/molecules/AskAiSearch";
import { PlansModal } from "../components/modals/PlansModal";
import { PlanTier, usePermissions } from "../utils/permissions";

const Dashboard = () => {
  const { t } = useTranslation();

  const { user, customer_identification, setUser } = useAuth();
  const navigate = useNavigate();

  const permissions = usePermissions(user);

  const [showAiSearchUpgradeModal, setShowAiSearchUpgradeModal] =
    useState(false);
  const [showWeakPassInfoModal, setShowWeakPassInfoModal] = useState(
    user.required_password_update || false
  );
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [showWhatsAppAddCreditModal, setShowWhatsAppAddCreditModal] =
    useState(false);
  const { mutate } = useCreateEntityPreferencesWithSpecialKey();

  const { data: userProfileData } = useGetEntityUser({
    customer_identification,
    entityIdentification: user.entity_identification,
    user_identification: customer_identification,
  });

  const { data: entityOwnerData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "sms_credit"
  );

  const { data: whatsappConfig } = useGetWhatsappConfig(
    customer_identification,
    user.entity_identification,
    permissions.hasFeatureAccess("SEND_WHATSAPP") &&
      permissions.can(Cap.SEND_WHATSAPP, Level.WRITE)
  );

  const { data: countData } = useGetCountUsers(
    customer_identification,
    user.entity_identification,
    { enabled: permissions.can(Cap.CUSTOMERS_LIST, Level.VIEW) }
  );
  const hasCustomers = (countData?.users ?? 0) > 0;

  const { data: entityStatusData } = useGetEntityStatus(
    customer_identification,
    user.entity_identification,
    {
      onSuccess: (result) => {
        let status = result?.entity_status;
        if (status === "NEW") {
          mutate({
            customer_identification: customer_identification,
            entityIdentification: user.entity_identification,
            userIdentification: customer_identification,
            key: "is_basic",
            value: true,
          });
          setUser({ ...user, isBasic: true });
        }
      },
    }
  );

  const getPersonalizedGreeting = () => {
    const hour = new Date().getHours();
    const day = new Date().getDay();
    const rawFirstName =
      userProfileData?.user_first_name || t("dashboard.greeting.defaultName");

    const firstName =
      rawFirstName.charAt(0).toUpperCase() +
      rawFirstName.slice(1).toLowerCase();

    let timeGreeting;

    if (hour >= 5 && hour < 12) {
      timeGreeting = t("dashboard.greeting.goodMorning");
    } else if (hour >= 12 && hour < 17) {
      timeGreeting = t("dashboard.greeting.goodAfternoon");
    } else if (hour >= 17 && hour < 24) {
      timeGreeting = t("dashboard.greeting.goodEvening");
    } else {
      timeGreeting = t("dashboard.greeting.lateNight");
    }

    if (day === 1) {
      return t("dashboard.greeting.monday", { timeGreeting, firstName });
    } else if (day === 5) {
      return t("dashboard.greeting.friday", { timeGreeting, firstName });
    } else if (day === 0 || day === 6) {
      return t("dashboard.greeting.weekend", { timeGreeting, firstName });
    } else {
      return t("dashboard.greeting.default", { timeGreeting, firstName });
    }
  };

  const statusLineKey = entityStatusData?.entity_status
    ? entityStatusLineKey(
        entityStatusData.entity_status,
        permissions.isOwner() ||
          permissions.can(Cap.ENTITY_SETTINGS, Level.WRITE)
      )
    : null;

  const renderWeakPassInfo = () => {
    return (
      <div className="text-center">
        <img
          title="Super User Manager"
          loading="lazy"
          src={
            "https://cdn.1cdn.app/application/UMD/22102511094783_weak_pass.svg"
          }
          className="img-fluid mb-3"
          alt="welcome to dashboard"
        />
        <h2 className="text-lg font-semibold text-gray-900">
          Your password is weak!
        </h2>
        <p className="mt-0.5 text-sm text-gray-500">
          To protect your account, change your password to a stronger one.
        </p>
      </div>
    );
  };

  const goToLevelUpPassword = () => {
    handleCancel();
    navigate("/level-up-password", { replace: true });
  };

  const handleCancel = () => {
    setShowWeakPassInfoModal(false);
    setUser({ ...user, required_password_update: false });

    if (typeof window !== "undefined") {
      SecureStorage.updateAuthData({ required_password_update: false });
    }
  };

  return (
    <div className="dashboard-page">
      <Helmet>
        <title>Dashboard | Super User Manager</title>
        <meta
          name="description"
          content="Super User Manager dashboard - control panel overview"
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-5 flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-gray-900">
            {getPersonalizedGreeting()}
          </h1>
          {statusLineKey &&
            !(
              entityStatusData?.entity_status === "SERVICE_ON" && hasCustomers
            ) && <p className="text-sm text-gray-500">{t(statusLineKey)}</p>}
          <SmsCreditWarning
            smsCredit={entityOwnerData?.entity_other_data?.sms_credit}
            onAddCredit={() => setShowAddCreditModal(true)}
          />
          <WhatsappCreditWarning
            whatsappCredit={whatsappConfig?.whatsapp_credit}
            configured={whatsappConfig?.configured}
            onAddCredit={() => setShowWhatsAppAddCreditModal(true)}
          />
        </div>

        {(!permissions.isRoleGoverned() ||
          permissions.can(Cap.AI_ENGINE_SEARCH_GLOBAL, Level.VIEW)) && (
          <div>
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("dashboard.askAi.heroTitle")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("dashboard.askAi.heroSubtitle")}
              </p>
            </div>
            <AskAiSearch
              locked={!permissions.hasFeatureAccess("AI_ENGINE")}
              onUnlock={() => setShowAiSearchUpgradeModal(true)}
            />
          </div>
        )}

        <div className="mb-5">
          <WelcomeHub />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-12 col-span-1">
            <PasskeyEnrolmentPrompt />
          </div>

          <TrialOfferBanner />

          <ActiveTrialBanner className="lg:col-span-12 col-span-1" />

          {permissions.can(Cap.ENTITY_SETTINGS, Level.VIEW) && (
            <div className="lg:col-span-12 col-span-1">
              <EntityCheck entity_identification={user.entity_identification} />
            </div>
          )}

          {permissions.can(Cap.CUSTOMERS_LIST, Level.VIEW) && (
            <div className="lg:col-span-12 col-span-1">
              <UsersStatsChart />
            </div>
          )}
        </div>
      </div>

      <PlansModal
        showPlansModal={showAiSearchUpgradeModal}
        setShowPlansModal={setShowAiSearchUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
      />

      <ModalShell
        show={showWeakPassInfoModal}
        onClose={() => handleCancel()}
        footer={
          <CustomButton
            variant="primary"
            onClick={() => goToLevelUpPassword()}
            title="Change password"
          />
        }
      >
        {renderWeakPassInfo()}
      </ModalShell>

      <AddCreditModal
        show={showAddCreditModal}
        onClose={() => setShowAddCreditModal(false)}
      />

      <AddCreditModal
        kind="whatsapp"
        show={showWhatsAppAddCreditModal}
        onClose={() => setShowWhatsAppAddCreditModal(false)}
      />
    </div>
  );
};

export default Dashboard;
